import LogoutIcon from '@mui/icons-material/Logout';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';

import { accountEntries } from '../navigation';

import { useLogOut } from './useLogOut';

/**
 * The account rail, down the right of the page on a wide screen, as the
 * wireframes draw it: the account pages this person may use, then Log Out.
 *
 * The same entries as the avatar menu, from the same helper, so the two never
 * disagree. Hiding an entry is a courtesy; the route guard is what refuses one.
 */
export function AccountRail() {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  const { pathname } = useLocation();
  const logOut = useLogOut();

  return (
    // A navigation landmark holding a real list, so each entry is an item of a
    // list a screen reader can count rather than a list item with no list.
    <Box component="nav" aria-label="Account">
      <List sx={{ py: 1 }}>
        {accountEntries(user?.role ?? null, Boolean(viewing)).map((item) => {
          const Icon = item.icon;
          const current = pathname === item.path;
          return (
            <li key={item.path} style={{ listStyle: 'none' }}>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={current}
                aria-current={current ? 'page' : undefined}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  slotProps={{ primary: { sx: { fontWeight: current ? 600 : 400 } } }}
                />
              </ListItemButton>
            </li>
          );
        })}

        {/* The line above Log Out is drawn inside its item, not as an item of
            its own: a separator in the list's place would be a list item that
            is not one. */}
        <li style={{ listStyle: 'none' }}>
          <Divider sx={{ my: 1 }} />
          <ListItemButton onClick={logOut}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Log Out" />
          </ListItemButton>
        </li>
      </List>
    </Box>
  );
}
